import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ nom: "", email: "", sujet: "", message: "", telephone: "" });
  const [loading, setLoading] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, sujet: e.detail }));
    window.addEventListener("prefill-sujet", handler);
    return () => window.removeEventListener("prefill-sujet", handler);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.toast.ok"), { description: t("contact.toast.ok.x") });
      setForm({ nom: "", email: "", sujet: "", message: "", telephone: "" });
    } catch {
      toast.error(t("contact.toast.error"), { description: t("contact.toast.error.x") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 lg:py-40 bg-[#121214]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("contact.eyebrow")}</p>
          <h2 className="font-display text-4xl md:text-5xl uppercase font-bold tracking-tight leading-tight">
            {t("contact.title.a")} <span className="text-[#C5A059]">{t("contact.title.b")}</span>
          </h2>
          <p className="text-lg leading-relaxed text-[#A1A1AA] mt-8">
            {t("contact.text")}
          </p>
          <div className="mt-12 space-y-6">
            <a href="mailto:contact@dream2realityfa.com" data-testid="contact-email-link" className="flex items-center gap-4 text-[#A1A1AA] hover:text-[#C5A059] transition-colors duration-300">
              <Mail size={18} className="text-[#C5A059]" />
              <span className="text-sm">contact@dream2realityfa.com</span>
            </a>
            <a href="https://wa.me/243846846760" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 text-[#A1A1AA] hover:text-[#C5A059] transition-colors duration-300">
              <MessageCircle size={18} className="text-[#C5A059]" />
              <span className="text-sm">WhatsApp : +243 846 846 760</span>
            </a>
            <a href="tel:+12267597679" data-testid="contact-phone-canada" className="flex items-center gap-4 text-[#A1A1AA] hover:text-[#C5A059] transition-colors duration-300">
              <Phone size={18} className="text-[#C5A059]" />
              <span className="text-sm">+1 (226) 759-7679 — Canada</span>
            </a>
            <a href="tel:+243846846760" data-testid="contact-phone-rdc" className="flex items-center gap-4 text-[#A1A1AA] hover:text-[#C5A059] transition-colors duration-300">
              <Phone size={18} className="text-[#C5A059]" />
              <span className="text-sm">+243 846 846 760 — Kinshasa, RDC</span>
            </a>
            <div className="flex items-center gap-4 text-[#A1A1AA]">
              <MapPin size={18} className="text-[#C5A059]" />
              <span className="text-sm">Kinshasa, RDC — Canada</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-10">
            <input data-testid="contact-input-nom" required placeholder={t("contact.f.name")} value={form.nom} onChange={set("nom")} className="input-editorial" />
            <input data-testid="contact-input-email" required type="email" placeholder={t("contact.f.email")} value={form.email} onChange={set("email")} className="input-editorial" />
            <input data-testid="contact-input-sujet" required placeholder={t("contact.f.subject")} value={form.sujet} onChange={set("sujet")} className="input-editorial" />
            <input data-testid="contact-input-telephone" type="tel" placeholder={t("contact.f.phone")} value={form.telephone} onChange={set("telephone")} className="input-editorial" />
            <textarea
              data-testid="contact-input-message"
              required
              rows={5}
              placeholder={t("contact.f.message")}
              value={form.message}
              onChange={set("message")}
              className="input-editorial sm:col-span-2 resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="group mt-12 inline-flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-10 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#C5A059] hover:text-black transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
            {t("contact.submit")}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
